/**
 * CSS variables generator.
 *
 * Turns the typed tokens into a flat CSS-custom-property map. ThemeProvider
 * applies the result to the document root so any component can reference
 * `var(--tensaw-color-text-primary)` etc. without importing a JS module.
 *
 * Variable naming convention:
 *   --tensaw-<group>-<role>             # e.g. --tensaw-color-text-primary
 *   --tensaw-<group>-<key>              # e.g. --tensaw-spacing-4
 *
 * The `tensaw-` prefix signals ownership: these are the public theming
 * contract that host tenants override. See `docs/DESIGN_TOKENS.md`.
 */

import { semantic, type SemanticColors } from './colors';
import { density, radius, spacing, typography, type Density } from './dimensions';
import { motion, shadow, zIndex } from './effects';

type CssVarMap = Record<string, string>;

function colorVars(colors: SemanticColors): CssVarMap {
  const out: CssVarMap = {};
  for (const [role, value] of Object.entries(colors)) {
    // Object.entries widens to `[string, any][]` against SemanticColors; the
    // values are always color strings, so cast at the assignment site.
    const kebabRole = kebab(role);
    out[`--tensaw-color-${kebabRole}`] = value as string;
    // Status alias: the typed semantic uses `statusError*`, but the standards
    // and new components use the canonical `status-danger-*` naming. Emit both
    // so existing code and new components both resolve. `error` is the
    // deprecated alias; remove in v3.1+ once consumer surface is migrated.
    if (kebabRole.startsWith('status-error-')) {
      const dangerKey = kebabRole.replace('status-error-', 'status-danger-');
      out[`--tensaw-color-${dangerKey}`] = value as string;
    }
  }
  return out;
}

function spacingVars(): CssVarMap {
  const out: CssVarMap = {};
  for (const [key, value] of Object.entries(spacing)) {
    out[`--tensaw-spacing-${key}`] = value;
  }
  return out;
}

function radiusVars(): CssVarMap {
  const out: CssVarMap = {};
  for (const [key, value] of Object.entries(radius)) {
    out[`--tensaw-radius-${key}`] = value;
  }
  return out;
}

function shadowVars(): CssVarMap {
  const out: CssVarMap = {};
  for (const [key, value] of Object.entries(shadow)) {
    out[`--tensaw-shadow-${kebab(key)}`] = value;
  }
  return out;
}

function motionVars(): CssVarMap {
  const out: CssVarMap = {};
  for (const [key, value] of Object.entries(motion.duration)) {
    out[`--tensaw-motion-duration-${kebab(key)}`] = value;
  }
  for (const [key, value] of Object.entries(motion.easing)) {
    out[`--tensaw-motion-easing-${kebab(key)}`] = value;
  }
  return out;
}

function zIndexVars(): CssVarMap {
  const out: CssVarMap = {};
  for (const [key, value] of Object.entries(zIndex)) {
    out[`--tensaw-z-${kebab(key)}`] = String(value);
  }
  return out;
}

function typographyVars(): CssVarMap {
  const out: CssVarMap = {};
  out['--tensaw-font-sans'] = typography.fontFamily.sans;
  out['--tensaw-font-mono'] = typography.fontFamily.mono;
  for (const [key, value] of Object.entries(typography.fontSize)) {
    out[`--tensaw-fs-${key}`] = value;
  }
  for (const [key, value] of Object.entries(typography.fontWeight)) {
    out[`--tensaw-fw-${key}`] = String(value);
  }
  for (const [key, value] of Object.entries(typography.lineHeight)) {
    out[`--tensaw-lh-${key}`] = String(value);
  }
  return out;
}

function densityVars(d: Density): CssVarMap {
  const cur = density[d];
  return {
    '--tensaw-density-row-height': cur.rowHeight,
    '--tensaw-density-input-height': cur.inputHeight,
    '--tensaw-density-button-height': cur.buttonHeight,
    '--tensaw-density-padding': cur.padding,
  };
}

export interface BuildVarsOptions {
  mode: 'light' | 'dark';
  density: Density;
}

export function buildCssVariables(options: BuildVarsOptions): CssVarMap {
  return {
    ...colorVars(semantic[options.mode]),
    ...spacingVars(),
    ...radiusVars(),
    ...shadowVars(),
    ...motionVars(),
    ...zIndexVars(),
    ...typographyVars(),
    ...densityVars(options.density),
  };
}

/** Apply a CSS variable map to a DOM element. */
export function applyCssVariables(target: HTMLElement, vars: CssVarMap): void {
  for (const [name, value] of Object.entries(vars)) {
    target.style.setProperty(name, value);
  }
}

/** Render the variables as a CSS string under a given selector. */
export function renderCssVariables(
  vars: CssVarMap,
  selector = ':root',
): string {
  const body = Object.entries(vars)
    .map(([name, value]) => `  ${name}: ${value};`)
    .join('\n');
  return `${selector} {\n${body}\n}\n`;
}

function kebab(s: string): string {
  return s.replace(/[A-Z]/g, (m, i: number) => (i === 0 ? m.toLowerCase() : `-${m.toLowerCase()}`));
}
